import React, { FC, useEffect, useState } from 'react';
import { usePlayer } from 'providers/player/PlayerContext';
import { useNavigate } from 'react-router';
import { formatDate, isFuture, isNow } from 'utils/fnDate';
import { Marker, TVSeriesDetailEpisode } from 'types/Asset';
import { getCatchupPlayerURL, getLivePlayerURL, getCatchupPlayOptionDetailURL, getBroadcastDetailsURL } from 'utils/fnUrl';
import useYouboraContentTracking, { ContentTrackingParams } from 'hooks/useYouboraContentTracking';
import { useDetailLocationParams } from 'hooks/useDetailLocationParams';
import { useAuth } from 'providers/useAuth/AuthContext';
import { StandardCover } from 'types/Cover';
import { CardContext } from 'types/Common';
import { ProgressIndicator } from 'components/ProgressIndicator/ProgressIndicator';
import { useConfig } from 'providers/useConfig/ConfigContext';
import { LiveProgressIndicator } from 'components/LiveProgressIndicator/LiveProgressIndicator';
import { useAssetReplayCapable } from 'hooks/useAssetReplayCapable/useAssetReplayCapable';
import { useBroadcastTimeCluster } from 'hooks/useBroadcastTimeCluster/useBroadcastTimeCluster';
import { useIsWithinChannelReplayDepth } from 'hooks/useIsWithinChannelReplayDepth/useIsWithinChannelReplayDepth';
import { usePlayOptionHelper } from 'providers/usePlayOptions/PlayOptionsContext';
import { CardTrackingData } from 'types/Tracking';
import { MILLISECONDS } from 'utils/TimeUnit';
import { getTVSeriesDetailContentMarker } from 'utils/fnContentMarker';
import { useApp } from 'providers/useApp/AppContext';
import { useChannels } from 'providers/useChannels/useChannels';
import { Card } from 'components/Cards/Card/Card';
import { CardVariant } from 'components/Cards/Card/cardVariants';
import { getPlayerPropsForUrl } from 'hooks/usePlayerRouter';

export const TVEpisodeCardPresenter: FC<{
    asset: TVSeriesDetailEpisode;
    cardContext: CardContext;
    trackingData: CardTrackingData;
    hideChannelLogo?: boolean;
    showDescription?: boolean;
    hideGenre?: boolean;
    onClick?: (event: React.MouseEvent<any>, data: TVSeriesDetailEpisode) => void;
}> = ({ asset, cardContext, onClick, trackingData, hideChannelLogo = false, showDescription = false, hideGenre = false }) => {
    const { id, episodeId, title, image, image_url, bookmark, isLive, description, listings, kind } = asset;
    const locationParamsPath = useDetailLocationParams();
    const { isGuest } = useAuth();
    const { appLanguage } = useApp();
    const navigate = useNavigate();
    const { config } = useConfig();
    const { getChannelById } = useChannels();

    const timeCluster = useBroadcastTimeCluster(asset);
    const playOptionVerifier = usePlayOptionHelper();
    const { isMini, updateMiniPlayerProps } = usePlayer();
    const { isReplayCapable } = useAssetReplayCapable(asset);
    const checkIsWithinChannelReplayDepth = useIsWithinChannelReplayDepth();

    const { handleContentFocus, handleContentFocusOut, handleContentClick, trackContentPlay } = useYouboraContentTracking();

    const [contentMarker, setContentMarker] = useState<Marker>(null);

    const isChannelSubscribed = (asset?.listings?.[0]?.replay_until ?? 0) > 0;

    const startSeconds = asset?.start ?? listings?.[0]?.start;
    const durationSeconds = asset?.duration ?? listings?.[0]?.duration;

    const startTime = startSeconds ? startSeconds * 1000 : null;
    const endTime = startSeconds && durationSeconds ? (startSeconds + durationSeconds) * 1000 : null;

    const channelId = asset?.listings?.[0]?.channelId ?? asset?.channelId;
    const channelLogo = hideChannelLogo ? undefined : getChannelById(channelId)?.logo;

    const isWithinChannelReplayDepth = checkIsWithinChannelReplayDepth({ channel_id: channelId, start: startSeconds });

    const formatTime = (time: number) => {
        const date = new Date(time);
        return date.toLocaleString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
    };

    const formatDateTime = (time: number) => {
        const date = new Date(time);
        return `${formatDate(date)} ${formatTime(time)}`;
    };

    const genreMetaData = hideGenre ? '' : asset.genres?.join(', ') || '';
    const metaDataWithTimeRange = startTime && endTime ? `${formatDateTime(startTime)} - ${formatTime(endTime)}` : genreMetaData;
    const metaDataWithStartTime = startTime ? formatDateTime(startTime) : genreMetaData;
    const metaData = endTime ? metaDataWithTimeRange : metaDataWithStartTime;

    const toPlayerPath = (onlyThePath: boolean = false) => {
        if (timeCluster === 'LIVE') {
            if (isMini) {
                if (onlyThePath) {
                    return null;
                }
                const playerProps = getPlayerPropsForUrl(getLivePlayerURL(channelId));

                if (playerProps) {
                    updateMiniPlayerProps(playerProps);
                }
            } else {
                if (onlyThePath) {
                    return getLivePlayerURL(channelId);
                }
                navigate(getLivePlayerURL(channelId));
            }
        } else {
            if (onlyThePath) {
                return getCatchupPlayerURL(asset?.listings[0].id);
            }
            playOptionVerifier(asset, getCatchupPlayerURL(asset?.listings[0].id));
        }

        return null;
    };

    const onPlayClicked = (event) => {
        event.preventDefault();
        event.stopPropagation();

        // Track TV episode play with contentId
        // All three method calls are needed for episodes
        trackContentPlay(asset.id, {
            contentLink: asset.id,
            contentLinkType: 'contentId',
        });

        const { seriesSeasonId, seasonId, seriesId } = asset as any;
        // Construct seriesSeasonId from available data if not provided
        // Priority: seriesSeasonId > seasonId > constructed from program_series_id/seriesId + season_number
        let effectiveSeasonId = seriesSeasonId || seasonId;

        if (!effectiveSeasonId) {
            const tvSeriesId = seriesId || asset.program_series_id;
            const seasonNum = asset.season_number;

            if (tvSeriesId && seasonNum != null) {
                effectiveSeasonId = `${tvSeriesId}_S${seasonNum}`;
            }
        }

        if (effectiveSeasonId) {
            trackContentPlay('null', {
                contentLink: effectiveSeasonId,
                contentLinkType: 'seriesSeasonId',
            });
        }

        if (seriesId || asset.program_series_id) {
            trackContentPlay('null', {
                contentLink: seriesId || asset.program_series_id,
                contentLinkType: 'seriesId',
            });
        }

        toPlayerPath(false);
    };

    const renderPlayIcon = () =>
        !isGuest && isChannelSubscribed && ((isReplayCapable && isWithinChannelReplayDepth) || isNow(startTime, endTime));

    const calculatePath = () => {
        if (kind === 'listing') {
            return getCatchupPlayOptionDetailURL(asset, appLanguage);
        }

        if (renderPlayIcon()) {
            return toPlayerPath(true);
        }

        const path = getBroadcastDetailsURL(asset, locationParamsPath, appLanguage);
        return path;
    };

    useEffect(() => {
        if (timeCluster && asset && startTime && endTime) {
            setContentMarker(getTVSeriesDetailContentMarker(timeCluster, isLive));
        } else {
            setContentMarker(null);
        }
    }, [timeCluster, asset]);

    const progress = (bookmark * 100) / MILLISECONDS.toSeconds(endTime - startTime);

    const getImage = () => image || image_url;

    const getYouboraParams = (): ContentTrackingParams => ({
        section: trackingData['data-module-title'] || '',
        sectionOrder: trackingData['data-stripe-position'] || 1,
        column: trackingData['data-card-position'] || 1,
        row: 1,
        contentId: asset.id,
        contentLink: asset.id,
        contentLinkType: 'contentId',
    });

    const handleCardClick = () => {
        handleContentClick(getYouboraParams());
    };

    const contentTrackingProps = {
        onMouseEnter: () => handleContentFocus(getYouboraParams()),
        onMouseLeave: handleContentFocusOut,
        onClick: (event: React.MouseEvent) => {
            handleCardClick();
            if (onClick) {
                onClick(event, asset);
            }
        },
    };

    const cover: StandardCover = {
        uuid: id + episodeId,
        id,
        title,
        image: getImage(),
        landscapeImage: getImage(),
        path: calculatePath(),
        metaData,
        shortDescription: description,
        circle: false,
        landscape: true,
        assetType: asset.type,
        hideDescription: !showDescription,
        rawData: asset,
        channelImage: channelLogo,
        contentMarker,
        progressBar:
            timeCluster === 'LIVE' ? (
                <LiveProgressIndicator startTime={startSeconds} endTime={startSeconds + durationSeconds} onLiveStateChanged={null} />
            ) : (
                !isFuture(startTime) && (
                    <ProgressIndicator percentage={progress < config.app_config.player_settings.bookmark_threshold ? progress : 0} />
                )
            ),
        renderPlayIcon: renderPlayIcon(),
        isEntitled: !isGuest,
        onPlayIconClicked: onPlayClicked,
    };

    const cardProps = {
        asset: cover,
        ...trackingData,
        ...contentTrackingProps,
        ...(timeCluster === 'FUTURE' && { 'data-upcoming': '' }),
    };

    return (
        <Card variant={CardVariant.LANDSCAPE} context={cardContext} showDescription={cardContext === CardContext.STRIPE} {...cardProps} />
    );
};
